/* eslint-disable import/prefer-default-export -- utils module may grow */
/**
 * Fire an io.analytics frontend event via the tracking client that <x-tracking-script>
 * loads (IOSUITE-7337). No-op when the tracker is absent — /admin, opt-out, or a missing
 * tenant/property mapping (the client then exposes an inert trackEvent). Category+action
 * must exist in the shared measurement schema (the client warns otherwise).
 *
 * `dimensions` sets per-event Matomo custom dimensions, keyed by the schema dimension KEY
 * (e.g. { module: 'order', 'functional-area': 'tariff_change' }). Unknown keys for the
 * active property are ignored by the client (a warning, never a throw).
 */
/** Suite apps whose io-less name is a valid `module` dimension value. */
const KNOWN_MODULES = new Set(['base', 'order', 'product', 'center', 'suite', 'api', 'sales']);

export function ioTrack(
  category: string,
  action: string,
  name?: string,
  dimensions?: Record<string, string>,
): void {
  // Auto-fill the `module` dimension from the category's app segment (base.* → base, product.* →
  // product, …), matching the backend IoAnalytics auto-fill. An explicit module (e.g. the order
  // wizard's order|sales|center) always wins; non-app segments (portal.*, corporate.*) get none.
  const dims: Record<string, string> = { ...(dimensions ?? {}) };
  if (!dims.module) {
    const mod = category.split('.')[0];
    if (KNOWN_MODULES.has(mod)) dims.module = mod;
  }
  const hasDims = Object.keys(dims).length > 0;

  const tracker = (window as unknown as {
    ioAnalytics?: {
      trackEvent?: (
        c: string,
        a: string,
        n?: string,
        v?: unknown,
        d?: Record<string, string>,
      ) => void,
      q?: unknown[],
    }
  }).ioAnalytics;

  // Callers sit directly in business flows (order submit, tariff selection, the tariff
  // calculation's promise chain) — an error from the external client must never reach them.
  try {
    if (tracker && typeof tracker.trackEvent === 'function') {
      // value is unused from the frontend helper; dimensions travel as the trailing arg.
      tracker.trackEvent(category, action, name, undefined, hasDims ? dims : undefined);
    } else if (tracker && Array.isArray(tracker.q)) {
      // v1.js (async) not loaded yet: the snippet's queue is replayed once it is — otherwise
      // events right after page load (e.g. `wizard_started`) would be dropped.
      tracker.q.push(['trackEvent', category, action, name, undefined, hasDims ? dims : undefined]);
    }
  } catch (e) {
    // tracking is best-effort
  }
}
